import * as app from 'durandal/app';
import * as system from 'durandal/system';
import * as router from 'plugins/router';
import * as ko from 'knockout';

import { LegacyFeatureFlag } from '@/features/feature-flags';
import LocationService from '@/features/location/services/locationService';
import BankLoginSource from '@/legacy/features/companies/bankLogin/bankLoginSource';
import SecondaryBankLoginFragment from '@/legacy/features/companies/bankLogin/secondary/SecondaryBankLoginFragment';
import { SecondaryBankAccountDetailsAddModel } from '@/legacy/features/companies/bankLogin/secondary/viewModels';
import VoidChequeEftDialog from '@/legacy/features/companies/emailMoneyTransfers/VoidChequeEftDialog';
import paymentTransactionErrorModal from '@/legacy/modals/styled/paymentTransactionError';
import titleMessageModal from '@/legacy/modals/styled/titleMessage';
import * as companyService from '@/legacy/services/company';
import * as bankLoginService from '@/legacy/services/company/bankLogin';
import * as payeeService from '@/legacy/services/company/payee';
import * as emailMoneyTransferService from '@/legacy/services/emailMoneyTransfer';
import * as flexSelectDataService from '@/legacy/services/flexSelectDataService';
import * as banksInfoService from '@/legacy/services/info/banks';
import * as plootoUtils from '@/legacy/utils/plooto';
import * as popupManager from '@/legacy/utils/popupManager';
import * as addModels from '@/legacy/viewmodels/addModels';

declareModules({
  'components/user/bank/bankInformationFields': () =>
    import('@/legacy/components/user/bank/bankInformationFields'),
  'components/user/bank/bankInformationFields.html': () =>
    import('@/legacy/components/user/bank/bankInformationFields.html'),
  'components/user/bank/bankValidation/bankSelection': () =>
    import('@/legacy/components/user/bank/bankValidation/bankSelection'),
  'components/user/bank/bankValidation/bankSelection.html': () =>
    import('@/legacy/components/user/bank/bankValidation/bankSelection.html'),
  'components/user/bank/bankValidation/bankValidationWrapper': () =>
    import('@/legacy/components/user/bank/bankValidation/bankValidationWrapper'),
  'components/user/bank/bankValidation/bankValidationWrapper.html': () =>
    import('@/legacy/components/user/bank/bankValidation/bankValidationWrapper.html'),
  'modals/manage/bankLoginDetails.html': () =>
    import('@/legacy/modals/manage/bankLoginDetails.html'),
});

export enum EmailMoneyTransferStep {
  Question,
  BankDetails,
  Confirmation,
  Success,
  Filled,
  Error,
  ReuseBank,
}
export class ViewModel {
  public router: DurandalRouter;

  public EmailMoneyTransferStep: typeof EmailMoneyTransferStep = EmailMoneyTransferStep;

  private emailMoneyTransferService: emailMoneyTransferService.EmailMoneyTransferService =
    emailMoneyTransferService.Instance;

  public status = ko.observable<string>('loading');

  private emailKey: string;

  public currentStep = ko.observable<EmailMoneyTransferStep>(EmailMoneyTransferStep.Question);

  public companyService = companyService.Instance;

  public emailMoneyTransfer: emailMoneyTransferService.EmailMoneyTransferViewModel;

  public emailMoneyTransferDetails: emailMoneyTransferService.EmailMoneyTransferDetailsViewModel;

  public account: payeeService.PayeeAccount;

  public bankName: string;

  public selectedAccountName: string;

  public reuseAccount = ko.observable<boolean>(null);

  public reuseForFuturePayments = ko.observable<boolean>(null);

  public bypassReusedInformation = ko.observable<boolean>(false);

  public optIntoMarketingCommunications = ko.observable<boolean>(false);

  public optedIntoMarketingCommunications: boolean;

  public institution: KnockoutObservable<string> = ko.observable('');

  public routingNumber: KnockoutObservable<string> = ko.observable('');

  public bankDetailsType: KnockoutObservable<string> = ko.observable('SelectBankingProvider');

  public statesAvailable = ko.observable<any>();

  public profileData: any; // payee data such as phone number, address

  public bankingSubmitData: addModels.EmailMoneyTransferBankAddModel;

  public errorDescription: string;

  public errorTitle: string;

  public voidChequeText = ko.observable<string>(null);

  private selectedAccount: bankLoginService.SelectedAccountBindingModel = null;

  public validateBankingInformation = true;

  private popupManager: popupManager.PopupManager = null;

  private bankAddModal: any; // This is the modal for entering bank credentials

  public bankProvider = ko.observable<banksInfoService.BankingProvider>(null);

  public bankLoginSource = BankLoginSource.EmailMoneyTransfer;

  public isSecondaryBankEnabled = false;

  public isSecondaryBankLoginModalVisible = ko.observable(false);

  private secondaryBankAccountDetailsAddModel: SecondaryBankAccountDetailsAddModel = null;

  public companyCountry = ko.observable<string>(null);

  public companyName = ko.observable<string>(null);

  public countrySelect = ko.observable<any>(null);

  public countryFlexSelectDataService: flexSelectDataService.FlexSelectDataService;

  public uniqueFormIdentifier = ko.observable(plootoUtils.GenerateRandomCharacters(8));

  public companyCountrySubscription: KnockoutSubscription;

  public getUniquieIdentifier = (propertyName: string): string =>
    `${this.uniqueFormIdentifier()}${propertyName}`;

  public bankCountryUniqueIdentifier = this.getUniquieIdentifier('bankCountry');

  public react = {
    fragments: {
      SecondaryBankLoginFragment,
      VoidChequeEftDialog,
    },
  };

  public showVoidChequeEftDialog = ko.observable<boolean>(false);

  constructor() {
    this.ResetClientData();

    this.router = router;
    this.account.country('CA');
    this.profileData = {};
    this.bindCountryFlexSelect();
  }

  public activate(emailKey: string) {
    this.AddPopupManager();

    const routeStep = this.GetRouteStep();

    if (this.currentStep() != routeStep) {
      this.currentStep(routeStep);
    }

    if (routeStep == EmailMoneyTransferStep.Question) {
      this.updateCurrentStep();
    }
    if (this.emailMoneyTransfer && this.emailMoneyTransfer.status == 'FILLED') {
      this.currentStep(EmailMoneyTransferStep.Filled);
    }

    // If we're looking at a new email transfer reset all client-entered data
    if (this.emailKey && emailKey === this.emailKey) {
      return;
    }

    this.ResetClientData();
    this.emailKey = emailKey;

    this.emailMoneyTransferService
      .GetByEmailKey(emailKey)
      .then((emailMoneyTransfer) => {
        this.account.securityAnswer('');
        if (emailMoneyTransfer.prefillCountry) {
          this.account.country(emailMoneyTransfer.prefillCountry);
        }
        if (emailMoneyTransfer.prefillCurrency) {
          this.account.currency(emailMoneyTransfer.prefillCurrency);
        }

        this.isSecondaryBankEnabled =
          emailMoneyTransfer.isSecondaryBankLoginEnabled ||
          plootoUtils.IsFeatureEnabled(LegacyFeatureFlag.SECONDARY_BANK_LOGIN);

        this.emailMoneyTransfer = emailMoneyTransfer;

        this.companyCountry(this.emailMoneyTransfer.companyCountry);
        this.companyName(this.emailMoneyTransfer.companyName);
        this.updateCurrentStep();

        this.status('loaded');
      })
      .fail((error) => {
        this.HandleError(error);
      });
  }

  public country: KnockoutComputed<any> = ko.pureComputed({
    read: () => this.account.country(),
    write: (value: string) => {
      this.account.country(value);
      LocationService.getCountrySupportedStates(this.account.country()).then((states) => {
        this.statesAvailable(states);
      });
    },
    deferEvaluation: true,
  });

  private updateCurrentStep() {
    if (this.emailMoneyTransfer) {
      if (this.emailMoneyTransfer.status === 'FILLED') {
        this.currentStep(EmailMoneyTransferStep.Filled);
      } else if (this.emailMoneyTransfer.skipSecurityQuestions) {
        this.reuseAccount(false);
        this.reuseForFuturePayments(false);
        this.account.securityAnswer(null);
        this.currentStep(EmailMoneyTransferStep.BankDetails);
      }
    }
  }

  public attached(view: HTMLElement, parent: HTMLElement): void {
    $('body').addClass('email-transfer');
    this.popupManager.attached();
    this.countryFlexSelectDataService.attached();
  }

  public detached(): void {
    $('body').removeClass('email-transfer');
    this.popupManager.detached();
    this.countryFlexSelectDataService?.detached();
    this.companyCountrySubscription?.dispose();
  }

  public canActivate(emailKey: string): any {
    const routeStep = this.GetRouteStep();
    // If we're looking at a different email transfer go back to the question
    if (
      this.emailKey &&
      emailKey != this.emailKey &&
      routeStep != EmailMoneyTransferStep.Question
    ) {
      return { redirect: this.GetStepUrl(emailKey, EmailMoneyTransferStep.Question) };
    }

    if (!this.emailKey || this.emailKey === emailKey) {
      const fixedRoute = this.GetFixedRouteStep(emailKey);
      if (fixedRoute) {
        return { redirect: fixedRoute };
      }
    }
    return true;
  }

  private ResetClientData() {
    this.status('loading');
    this.emailMoneyTransfer = null;
    this.emailKey = null;
    this.emailMoneyTransferDetails = null;
    this.bankingSubmitData = null;
    this.account = new payeeService.PayeeAccount(null, {} as any);
  }

  private GetRouteStep() {
    const routeParts = router.activeInstruction().fragment.split('/');
    const routeStep = routeParts[routeParts.length - 1];

    switch (routeStep) {
      case 'bankDetails':
        return EmailMoneyTransferStep.BankDetails;
      case 'reuseBank':
        return EmailMoneyTransferStep.ReuseBank;
      case 'confirmation':
        return EmailMoneyTransferStep.Confirmation;
      case 'success':
        return EmailMoneyTransferStep.Success;
      case 'error':
        return EmailMoneyTransferStep.Error;
    }

    return EmailMoneyTransferStep.Question;
  }

  // Ensure that each step can be accessible
  private GetFixedRouteStep(emailKey: string) {
    const routeStep = this.GetRouteStep();

    switch (routeStep) {
      case EmailMoneyTransferStep.BankDetails:
        if (!this.emailMoneyTransferDetails) {
          return this.GetStepUrl(emailKey, EmailMoneyTransferStep.Question);
        }
        break;
      case EmailMoneyTransferStep.ReuseBank:
        if (!this.emailMoneyTransferDetails) {
          return this.GetStepUrl(emailKey, EmailMoneyTransferStep.Question);
        }
        break;
      case EmailMoneyTransferStep.Confirmation:
        if (!this.bankingSubmitData) {
          return this.GetStepUrl(emailKey, EmailMoneyTransferStep.Question);
        }
        break;
      case EmailMoneyTransferStep.Success:
        if (!this.bankingSubmitData) {
          return this.GetStepUrl(emailKey, EmailMoneyTransferStep.Question);
        }
        break;
    }
    return null;
  }

  public AnswerStepTransferQuestion(form: HTMLFormElement) {
    if (!plootoUtils.ValidateForm(form)) {
      return;
    }

    plootoUtils.DisableForm(form);
    plootoUtils.HandleFormSubmit(form);

    this.emailMoneyTransferService
      .AnswerSecurityQuestion(this.emailKey, this.account.securityAnswer())
      .then((emailMoneyTransferDetails) => {
        this.emailMoneyTransferDetails = emailMoneyTransferDetails;
        this.account.country(emailMoneyTransferDetails.country);
        this.account.currency(emailMoneyTransferDetails.currency);
        this.reuseAccount(false);
        this.reuseForFuturePayments(true);
        if (this.emailMoneyTransferDetails.canReuseAccount) {
          this.reuseAccount(null);
          this.ActivateStep(EmailMoneyTransferStep.ReuseBank);
        } else {
          this.ActivateStep(EmailMoneyTransferStep.BankDetails);
        }

        plootoUtils.EnableForm(form);
      })
      .fail((error) => {
        this.HandleSubmitError(form, error);
      });
  }

  public SubmitBankingInformation(form: HTMLFormElement) {
    const submitData: any = {};

    // Form is not present on a new secondary bank login (as a user clicks a button that opens up a dialog)
    if (form) {
      if (!plootoUtils.ValidateForm(form)) {
        return;
      }

      const formData = $(form).serializeArray();
      $.each(formData, (i, field: any) => {
        submitData[field.name] = field.value;
      });

      plootoUtils.DisableForm(form);
      plootoUtils.HandleFormSubmit(form);
    }

    submitData.institutionOther = undefined;
    submitData.securityAnswer = this.account.securityAnswer();

    const emailMoneyTransferBankAddModel = {
      securityAnswer: submitData.securityAnswer,

      reuseAccount: this.reuseAccount(),
      bypassReusedInformation: this.bypassReusedInformation(),
    } as addModels.EmailMoneyTransferBankAddModel;

    if (this.secondaryBankAccountDetailsAddModel) {
      emailMoneyTransferBankAddModel.accountDetails = this.secondaryBankAccountDetailsAddModel;

      // for EMT since currency and country is specified by the company we know what they are.
      // In the backend we will throw if the currency of account doesn't match the EMT currency.
      emailMoneyTransferBankAddModel.accountDetails.country = this.account.country();
      emailMoneyTransferBankAddModel.accountDetails.currency = this.account.currency();
    } else if (this.selectedAccount) {
      const verifiedBankAccountDetailsAddModel = new addModels.VerifiedBankAccountDetailsAddModel();
      verifiedBankAccountDetailsAddModel.country = this.account.country();
      verifiedBankAccountDetailsAddModel.currency = this.account.currency();

      verifiedBankAccountDetailsAddModel.storageToken = this.selectedAccount.storageToken;
      verifiedBankAccountDetailsAddModel.selectedBankAccountId =
        this.selectedAccount.selectedBankAccountId;
      verifiedBankAccountDetailsAddModel.sessionToken = this.selectedAccount.sessionToken;

      emailMoneyTransferBankAddModel.accountDetails = verifiedBankAccountDetailsAddModel;
    } else {
      switch (this.account.country()) {
        case 'US':
          emailMoneyTransferBankAddModel.accountDetails = {
            currency: 'USD',
            country: 'US',
            accountNumber: submitData.accountNumber,
            routingNumber: submitData.routingNumber,
            validate: this.validateBankingInformation,
          } as addModels.UsBankAccountDetailsAddModel;
          break;
        case 'CA':
          emailMoneyTransferBankAddModel.accountDetails = {
            currency: this.account.currency(),
            country: 'CA',
            accountNumber: submitData.accountNumber,
            institution: submitData.institution,
            transit: submitData.transit,
            validate: this.validateBankingInformation,
          } as addModels.CanadianBankAccountDetailsAddModel;
          break;
      }
    }

    this.bankingSubmitData = emailMoneyTransferBankAddModel;
    this.bankingSubmitData.confirmed = false;

    this.emailMoneyTransferService
      .SubmitBankingInformation(this.emailKey, this.bankingSubmitData)
      .then((viewModel) => {
        this.bankName = viewModel.bankName;
        this.selectedAccountName = viewModel.selectedAccountName;
        this.optedIntoMarketingCommunications = viewModel.optedIntoMarketingCommunications;

        this.ActivateStep(EmailMoneyTransferStep.Confirmation);

        plootoUtils.EnableForm(form);
      })
      .fail((errorInfo) => {
        this.isSecondaryBankLoginModalVisible(false); // Hide modal for bank login on any error
        this.HandleSubmitError(form, errorInfo);
        if (errorInfo.error && errorInfo.type) {
          switch (errorInfo.type) {
            case 'region.unsupported':
              app.showMessage(errorInfo.message, 'Account Error');
              break;
            case 'invalid.parameter': {
              // if user has been warned about invalid bank account once, dont re-validate bank info next time
              const invalidAccountError = $.grep(
                errorInfo.fields,
                (errorField: any) => errorField.name == 'accountNumber'
              );
              if (invalidAccountError.length > 0) {
                this.validateBankingInformation = false;
              }
              break;
            }
          }
        }
      });
  }

  public SubmitConfirmation(form: HTMLFormElement) {
    plootoUtils.DisableForm(form);
    plootoUtils.HandleFormSubmit(form);

    this.bankingSubmitData.confirmed = true;
    this.bankingSubmitData.reuseForFuturePayments = this.reuseForFuturePayments();
    this.bankingSubmitData.optInToMarketingCommunications = this.optIntoMarketingCommunications();
    this.emailMoneyTransferService
      .SubmitBankingInformation(this.emailKey, this.bankingSubmitData)
      .then(() => {
        this.emailMoneyTransfer.status = 'FILLED';
        this.ActivateStep(EmailMoneyTransferStep.Success);

        plootoUtils.EnableForm(form);
      })
      .fail((error) => {
        if (error && error.type == 'region.unsupported') {
          app.showMessage(error.message, 'Account Error');
          return;
        }
        this.HandleSubmitError(form, error);
      });
  }

  public useAnotherBankAccount() {
    this.bankDetailsType('SelectBankingProvider');
    this.routingNumber(null);
    this.institution(null);
    this.currentStep(EmailMoneyTransferStep.BankDetails);
  }

  private GetStepUrl(emailKey: string, step: EmailMoneyTransferStep) {
    let stepUrl = '';
    switch (step) {
      case EmailMoneyTransferStep.Question:
        stepUrl = '';
        break;
      case EmailMoneyTransferStep.BankDetails:
        stepUrl = 'bankDetails';
        break;
      case EmailMoneyTransferStep.ReuseBank:
        stepUrl = 'reuseBank';
        break;
      case EmailMoneyTransferStep.Confirmation:
        stepUrl = 'confirmation';
        break;
      case EmailMoneyTransferStep.Success:
        stepUrl = 'success';
        break;
      case EmailMoneyTransferStep.Error:
        stepUrl = 'error';
        break;
    }
    return `emailmoneytransfer/accept/${emailKey}/${stepUrl}`;
  }

  private HandleSubmitError(form: HTMLFormElement, error: any) {
    plootoUtils.EnableForm(form);
    plootoUtils.HandleFormError(form, error);

    this.HandleError(error);
  }

  private HandleError(error: any) {
    if (error && error.type) {
      switch (error.type) {
        case 'region.unsupported':
          return;
        case 'blacklisted.account':
          this.DisplayError(
            'This account cannot be created at this time. Please contact compliance@plooto.com.',
            'Account Error'
          );
          return;
        case 'bankLogin.timeout':
          titleMessageModal.show({
            title: 'Session Timed Out',
            message: 'Bank provider session timed out. Please try again.',
            svgIconSymbolLarge: 'svg-warning-large',
          } as any);
          this.backToBankDetails();
          return;
        case 'invalid.securityAnswer':
          titleMessageModal.show({
            title: 'Invalid Security Answer',
            message:
              'Please double-check your security answer. If you do not know the answer to this security question please contact the sender.',
            svgIconSymbolLarge: 'svg-warning-large',
          } as any);
          return;
        case 'addAccount.notSufficientInfo':
          this.currentStep(EmailMoneyTransferStep.BankDetails); // Take person back to void check step as this can happen on confirmation page
          this.forceVoidChequeInput(
            'The selected account did not return certain information that is mandatory in order to be used by Plooto. Please verify your account by entering your cheque details instead.'
          );
          return;
        case 'invalid.securityAnswerEmpty':
          this.DisplayError(
            'Security question is missing. Please contact your payer to add security question/answer to this transfer.',
            'Incomplete Transfer'
          );
          return;
        case 'invalid.reusedInformation':
          paymentTransactionErrorModal
            .show({
              title: 'Bank Details Confirmation',
              transactionError: error.transactionError,
              svgIconSymbolLarge: 'svg-warning-large',
              continueButtonLabel: 'I Understand',
            } as any)
            .then((modalResponse) => {
              if (modalResponse) {
                this.bypassReusedInformation(true);
              }
            });
          return;
        case 'invalid.payment.status':
          this.DisplayError(
            'Invalid email transfer status. Payment status must be PENDING.',
            'Expired Email Transfer'
          );
          return;
        case 'already.completed':
          this.DisplayError(
            'This email transfer information is already complete',
            'Expired Email Transfer'
          );
          return;
        case 'invalid.bankCurrency':
          this.DisplayError(error.message, 'Expired Email Transfer');
          return;
        case 'invalid.account':
          this.DisplayError(
            'Your account country does not match payee account country',
            'Invalid Entry'
          );
          return;
        case 'invalid.attempts':
          this.DisplayError(
            'Too many invalid answers. Please contact the sender to update security question & answer to unlock this email transfer.',
            'Email Transfer Locked'
          );
          return;
        case 'invalid.emailMoneyTransfer':
          this.DisplayError(
            'Email transfer not found. Please double-check your email link.',
            'Invalid Email Transfer'
          );
          return;
        case 'invalid.payeeAccount.deleted':
          this.DisplayError(
            'The email money transfer account associated with this payment has been deleted. Please contact the sender for assistance.',
            'Email Money Transfer Account Deleted'
          );
          return;
        case 'invalid.emailMoneyTransfer.expired':
          this.DisplayError(
            'Email money transfer expired. Please double-check your email for expiry dates.',
            'Expired Email Transfer'
          );
          return;
        case 'invalid.payment':
          this.DisplayError(
            'Email transfer was recalled or voided. Please check your email for details.',
            'Expired/Voided Transfer'
          );
          return;

        case 'invalid.companyCapabilities':
          this.DisplayError(
            "It looks like this payment isn't ready to be accepted yet. Contact the sender of this payment for further details.",
            'Payment Pending...'
          );
          return;
        case 'invalid.parameter':
          return;
      }
    }

    titleMessageModal.show({
      title: 'Unknown error',
      message: `Error occurred with this email money transfer. Please try again later. (${
        error && error.type ? error.type : 'no.error.type'
      })`,
      svgIconSymbolLarge: 'svg-warning-large',
    } as any);
  }

  private DisplayError(errorDescription: string, errorTitle: string) {
    this.errorDescription = errorDescription;
    this.errorTitle = errorTitle;
    this.currentStep(EmailMoneyTransferStep.Error);
  }

  private ActivateStep(step: EmailMoneyTransferStep) {
    // Reset answer once we get to confirmation or error page
    if (step == EmailMoneyTransferStep.Success || step == EmailMoneyTransferStep.Error) {
      this.account.securityAnswer('');
    } else if (step == EmailMoneyTransferStep.BankDetails) {
      this.bankDetailsType('SelectBankingProvider');
    }
    router.navigate(this.GetStepUrl(this.emailKey, step));
  }

  public JSBindReuseAccountToggle(_, event): any {
    this.reuseAccount(Boolean(Number(event.target.value)));
  }

  public BankingInformationExists() {
    return (
      !this.emailMoneyTransfer.skipSecurityQuestions &&
      ((this.emailMoneyTransferDetails.canReuseAccount && this.reuseAccount() !== null) ||
        !this.emailMoneyTransferDetails.canReuseAccount)
    );
  }

  public backToBankDetails() {
    router.navigate(this.GetStepUrl(this.emailKey, EmailMoneyTransferStep.BankDetails));
  }

  public viewMyPayments() {
    router.navigate(`contacts/mypayments/${this.emailMoneyTransfer.payeeEmailKey}`);
  }

  public bankAccountSelected(
    selectedAccount: bankLoginService.SelectedAccountBindingModel,
    form: HTMLFormElement
  ) {
    this.selectedAccount = selectedAccount;

    this.SubmitBankingInformation(form);
  }

  public SubmitReuseBank(form: HTMLFormElement) {
    if (this.reuseAccount() === false) {
      this.ActivateStep(EmailMoneyTransferStep.BankDetails);
      return;
    }
    this.SubmitBankingInformation(form);
  }

  public forceVoidChequeInput(voidChequeText: string, country: string = null) {
    this.hideSelectDepositMethod();

    this.voidChequeText(voidChequeText);
    if (country) {
      this.account.country(country);
    }
    this.selectedAccount = null; // Clear previously selected account (Yodlee)

    this.secondaryBankAccountDetailsAddModel = null; // Clear previously selected secondary bank account (Flinks)
    this.isSecondaryBankLoginModalVisible(false); // Hide the popup for bank login dialog

    this.bankDetailsType('ManualEntry');
  }

  public bankingProviderSelected(bankProvider: banksInfoService.BankingProvider) {
    this.bankProvider(bankProvider);

    // User selected "Other / My bank is not listed above"
    if (!bankProvider) {
      this.forceVoidChequeInput(null);
      return;
    }

    // User selected bank that did not support login
    if (!bankProvider.bankInfo.supportsBankLogin) {
      this.forceVoidChequeInput(null);
      return;
    }

    // User selected bank that supports login. We'll force a popup without changing the url
    this.bankAddModal.modalParamsCallback = () => ({
      bankIdentifier: bankProvider.providerId,
      bankCountry: bankProvider.country,
      forceVoidChequeCallback: (voidChequeText: string) => {
        this.forceVoidChequeInput(voidChequeText, null);
        this.popupManager.closeExistingModal();
      },

      bankLoginFormType: 'EmailMoneyTransfer',
      bankLoginRelatedId: this.emailMoneyTransfer.id,
    });
    this.popupManager.forceShow(this.bankAddModal);
  }

  public shouldHideCountry() {
    if (this.emailMoneyTransfer.bankDetailsRequired) {
      return false;
    }

    return true;
  }

  public showSelectDepositMethod(): void {
    this.showVoidChequeEftDialog(true);
  }

  public hideSelectDepositMethod(): void {
    this.showVoidChequeEftDialog(false);
  }

  public showSecondaryBankLogin() {
    this.isSecondaryBankLoginModalVisible(true);
  }

  public async accountSelectedCallback(
    secondaryBankAccountDetailsAddModel: SecondaryBankAccountDetailsAddModel
  ) {
    console.log('account selected:', secondaryBankAccountDetailsAddModel);
    this.secondaryBankAccountDetailsAddModel = secondaryBankAccountDetailsAddModel;

    this.SubmitBankingInformation(null);
  }

  public closeNewBankDialog() {
    this.isSecondaryBankLoginModalVisible(false);
  }

  public handleEnterBankAccountDetailsClick(): void {
    this.forceVoidChequeInput(null);
  }

  public shouldShowBankInfo() {
    const accountDetails = this.bankingSubmitData.accountDetails as any;
    if (!accountDetails) {
      return false;
    }

    // Don't show bank info when using Yodlee to connect (Only show bank name)
    if (accountDetails.selectedBankAccountId) {
      return false;
    }

    // Don't show bank info (transit,account number etc) when using Flinks to connect (Only show bank name)
    if (this.secondaryBankAccountDetailsAddModel) {
      return false;
    }

    return true;
  }

  private AddPopupManager() {
    this.popupManager = new popupManager.PopupManager();

    // Special modal without a dialog url
    const bankAddModal = {
      modalHtmlPath: 'modals/manage/bankLoginDetails.html',

      success: (selectedAccount: bankLoginService.SelectedAccountBindingModel) => {
        const formSubmitBankingInformation = $(
          '#form-submit-banking-information'
        )[0] as HTMLFormElement;
        this.bankAccountSelected(selectedAccount, formSubmitBankingInformation);

        this.popupManager.closeExistingModal();
      },

      goBackCallback: () => {
        this.popupManager.closeExistingModal();
      },
    };
    this.popupManager.AddModal(bankAddModal as any);

    this.bankAddModal = bankAddModal;
  }

  private bindCountryFlexSelect() {
    // Set default country for bank selection
    const setDefaultCountry = () => {
      this.companyCountrySubscription = this.companyCountry.subscribe((companyCountry) => {
        switch (companyCountry) {
          case 'CA':
            this.countrySelect({ value: 'CA', text: 'Canada' });
            break;

          case 'US':
            this.countrySelect({ value: 'US', text: 'United States' });
            break;
        }
      });
    };

    const initializeCountrySelectionFlexSelect = () => {
      // Country selection
      const countryFlexSelectDataService = new flexSelectDataService.FlexSelectDataService();
      countryFlexSelectDataService.queryPromiseCallback = (searchQuery, flexSelect) =>
        system
          .defer<Array<any>>((dfd) => {
            const countries = LocationService.getPrimaryCountries();
            const filteredItems = [];
            countries.forEach((country) => {
              if (
                !searchQuery ||
                (searchQuery &&
                  (country.text.toLowerCase().indexOf(searchQuery.toLowerCase()) >= 0 ||
                    country.value.toLowerCase().indexOf(searchQuery.toLowerCase()) === 0))
              ) {
                filteredItems.push(country);
              }
            });

            dfd.resolve(filteredItems);
          })
          .promise();

      countryFlexSelectDataService.selectCallback = (
        filterToUpdate: any,
        koFilterValueValue: KnockoutObservable<any>,
        newFilterValue: any
      ) => {
        if (this.countrySelect() && this.countrySelect().value == newFilterValue.value) {
          return;
        }

        koFilterValueValue(newFilterValue);

        this.countrySelect(newFilterValue);
        this.account.country(newFilterValue.value);
      };

      this.countryFlexSelectDataService = countryFlexSelectDataService;
    };

    setDefaultCountry();
    initializeCountrySelectionFlexSelect();
  }
}
